import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { ModerationQueuePage } from '../_shared/types';

/**
 * The pre-publication moderation queue — held comments, oldest first.
 *
 * **`current_page` is the only parameter sent.** The endpoint also accepts a
 * page size and this surface does not name one: the service owns it, capped at
 * `MODERATION_QUEUE_PAGE_SIZE_MAX`, and a number chosen here would put a
 * server-owned value behind a rebuild of this bundle. No sort control, no
 * selection column and no filter strip either — those are notification-inbox
 * rows, and drawing them here would be borrowing another surface's scope. The
 * queue stays deliberately one decision at a time.
 *
 * The page **is** walked, because the queue is worked to the bottom: a held
 * comment is invisible to everyone but its author until someone judges it, so a
 * page nobody can reach is a comment that never appears and never gets refused
 * either.
 *
 * The page carries `meta.total_held` — the same figure the console entry's
 * dashboard tile reports, returned from one query so the two surfaces cannot
 * disagree. Both move together whenever an item is handled, and both count the
 * whole queue rather than the page in hand.
 */
export function moderationQueueKey(page: number) {
  return key('engagement', 'moderationQueue', { page });
}

/**
 * Every page of the queue at once — what a decision invalidates.
 *
 * A decision removes a row from the queue, which re-slices **every** page after
 * the one it was on: invalidating only the page in hand would leave the operator
 * walking forward into rows the service has already shifted.
 */
export function moderationQueueRootKey() {
  return key('engagement', 'moderationQueue');
}

export function useModerationQueue(page: number) {
  return useQuery({
    queryKey: moderationQueueKey(page),
    queryFn: () => api.get<ModerationQueuePage>('engagement', `/admin/comments?current_page=${page}`),
    // A queue's depth is what an operator came back to the tab for.
    staleTime: STALE_TIME.list,
    // Walking a page keeps the cards in place while the next set loads. The
    // queue's own focus restoration keys on `items.length`, and a blink through
    // an empty render would move focus twice.
    placeholderData: keepPreviousData,
  });
}
